import React, { useState, useEffect } from 'react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { cn } from '../lib/utils'

export function LoginForm({
  className,
  mode = 'login',
  formData,
  onInputChange,
  onSubmit,
  onToggleMode,
  isLoading,
  error,
  ...props
}) {
  const [focusedField, setFocusedField] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loadingDots, setLoadingDots] = useState(0);

  // Animated loading dots
  useEffect(() => {
    if (!isLoading) {
      setLoadingDots(0);
      return;
    }

    const interval = setInterval(() => {
      setLoadingDots(prev => (prev + 1) % 4);
    }, 500);

    return () => clearInterval(interval);
  }, [isLoading]);

  const loadingMessages = [
    "Slicing through the login process",
    "Preparing your slice of content",
    "Almost ready to serve",
    "Welcome to the pizza party"
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
    
    // Show success animation (you'd typically do this after successful response)
    // setShowSuccess(true);
    // setTimeout(() => setShowSuccess(false), 3000);
  };
  return (
    <form className={cn("flex flex-col gap-6 animate-fade-in-up", className)} onSubmit={handleSubmit} {...props}>
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">
          {mode === 'login' ? 'Login to your account' : 'Create your account'}
        </h1>
        <p className="text-muted-foreground text-sm text-balance">
          {mode === 'login'
            ? 'Enter your email below to login to your account'
            : 'Enter your details below to create your account'
          }
        </p>
        {error && (
          <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md border border-destructive/20 w-full animate-slide-in-right">
            <div className="flex items-center gap-2">
              <span className="text-lg">🍕</span>
              <span>Oops! That slice didn't quite work out. {error}</span>
            </div>
          </div>
        )}
        {showSuccess && (
          <div className="bg-primary/15 text-primary text-sm p-3 rounded-md border border-primary/20 w-full animate-slide-in-right success-bubble">
            <div className="flex items-center gap-2">
              <span className="text-lg animate-pizza-rotate">🍕</span>
              <span>Welcome to the slice! Logging you in...</span>
            </div>
          </div>
        )}
      </div>
      
      <div className="grid gap-6">
        {mode === 'register' && (
          <div className="grid gap-3">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={onInputChange}
              onFocus={() => setFocusedField('name')}
              onBlur={() => setFocusedField(null)}
              className={cn(
                "transition-all duration-200",
                focusedField === 'name' && "ring-2 ring-primary/50 border-primary scale-[1.02]"
              )}
              required
            />
          </div>
        )}
        
        <div className="grid gap-3">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="slice@example.com"
            value={formData.email}
            onChange={onInputChange}
            onFocus={() => setFocusedField('email')}
            onBlur={() => setFocusedField(null)}
            className={cn(
              "transition-all duration-200",
              focusedField === 'email' && "ring-2 ring-primary/50 border-primary scale-[1.02]"
            )}
            required
          />
        </div>
        
        <div className="grid gap-3">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={onInputChange}
            onFocus={() => setFocusedField('password')}
            onBlur={() => setFocusedField(null)}
            className={cn(
              "transition-all duration-200",
              focusedField === 'password' && "ring-2 ring-primary/50 border-primary scale-[1.02]"
            )}
            required
          />
          {mode === 'login' && (
            <a
              href="/forgot-password"
              tabIndex={-1}
              className="text-sm underline-offset-4 hover:underline w-fit"
            >
              Forgot your password?
            </a>
          )}
        </div>

        {mode === 'register' && (
          <div className="grid gap-3">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={onInputChange}
              required
            />
          </div>
        )}


        {mode === 'register' && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="acceptTerms"
              checked={formData.acceptTerms}
              onCheckedChange={(checked) =>
                onInputChange({ target: { name: 'acceptTerms', type: 'checkbox', checked } })
              }
              required
            />
            <Label htmlFor="acceptTerms" className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              I agree to the{' '}
              <a href="/terms" target="_blank" className="text-primary hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" className="text-primary hover:underline">
                Privacy Policy
              </a>
            </Label>
          </div>
        )}
        
        <Button 
          type="submit" 
          className={cn(
            "w-full transition-all duration-200 hover:scale-[1.02] hover:shadow-lg click-effect",
            isLoading && "loading-button",
            showSuccess && "animate-button-success"
          )} 
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="pizza-loading" />
              <span className="animate-typewriter">
                {loadingMessages[Math.floor(loadingDots)] || 'Processing'}{''.padEnd(loadingDots, '.')}
              </span>
            </div>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <span className="text-lg">🍕</span>
              {mode === 'login' ? 'Sign In' : 'Join Kinora'}
            </span>
          )}
        </Button>
        
        <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
          <span className="relative z-10 bg-background px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
        
        <Button 
          variant="outline" 
          className="w-full hover:scale-[1.02] transition-all duration-200 hover:shadow-md" 
          disabled
        >
          <svg width="20" height="20" viewBox="0 0 24 24" className="mr-2">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {mode === 'login' ? 'Quick slice with Google' : 'Join with Google'}
        </Button>
        
      </div>
      
      <div className="text-center text-sm">
        {mode === 'login' ? "Don't have an account?" : "Already have an account?"}{" "}
        <button
          type="button"
          onClick={onToggleMode}
          className="underline underline-offset-4 hover:text-primary"
        >
          {mode === 'login' ? 'Sign up' : 'Sign in'}
        </button>
      </div>
    </form>
  )
}